import React, { useContext, useState, useEffect } from 'react';
import { notification } from 'antd';
import { AppContext } from 'App.context';
import { ApiContext } from 'utils/networking/Api.context';
import { PDFDocument } from 'pdf-lib';

export default function useHbxUploadExampleInvoices() {
  const { API } = useContext(ApiContext);
  const {
    appState,
    appActions: { getSelectedHotel, translate },
  } = useContext(AppContext);

  const [state, setState] = useState({
    isLoading: false,
    existsSingle: false,
    existsMultiple: false,
    suscription: null /* null | active | requested */,
    adviceDeleteFile: false,
    type: null,
  });

  const validatePdf = async (file, minPages) => {
    const buffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(buffer);
    const pages = pdfDoc.getPageCount();

    if (minPages === 1 && pages !== 1) {
      notification.error({
        message: translate('hbxProducts.HbxUploadExampleInvoices.errorSinglePage'),
      });
      return false;
    }

    if (minPages > 1 && pages < minPages) {
      notification.error({
        message: translate('hbxProducts.HbxUploadExampleInvoices.errorMinPages'),
      });
      return false;
    }

    return true;
  };

  const checkFileExists = async type => {
    try {
      const hotel = await API.getOneHotel({ hotelId: getSelectedHotel()?.key });

      const fileBuffer = await API.invoiceTemplateCheckFileExists(hotel.id, hotel.chainId, type);

      return fileBuffer && fileBuffer.data ? true : false;
    } catch (err) {
      console.error('Error checking file existence:', err);
      return false;
    }
  };

  const uploadFile = async (file, type, minPages) => {
    const valid = await validatePdf(file, minPages);
    if (!valid) return false;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const hotel = await API.getOneHotel({ hotelId: getSelectedHotel()?.key });

      const response = await API.invoiceUploadInvoiceSample(hotel.id, hotel.chainId, file, type);

      if (response?.httpStatusCode === 200) {
        notification.success({
          message: translate('hbxProducts.HbxUploadExampleInvoices.fileUploaded'),
        });

        if (type === 'single') {
          setState(prev => ({ ...prev, existsSingle: true }));
        } else if (type === 'multiple') {
          setState(prev => ({ ...prev, existsMultiple: true }));
        }

        return true;
      }

      notification.error({
        message: translate('hbxProducts.HbxUploadExampleInvoices.fileNotUploaded'),
      });
      return false;
    } catch (err) {
      console.error(err);
      notification.error({
        message: translate('hbxProducts.HbxUploadExampleInvoices.errorMessageUpload'),
      });
      return false;
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const downloadFile = async type => {
    try {
      const hotel = await API.getOneHotel({ hotelId: getSelectedHotel()?.key });

      const fileBuffer = await API.invoiceTemplateCheckFileExists(hotel.id, hotel.chainId, type);

      if (!fileBuffer?.data) {
        notification.error({
          message: translate('hbxProducts.HbxUploadExampleInvoices.fileNotFound'),
        });
        return;
      }

      const array = new Uint8Array(fileBuffer.data);
      const blob = new Blob([array], {
        type: 'application/pdf',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${type}.pdf`;
      a.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      notification.error({
        message: translate('hbxProducts.HbxUploadExampleInvoices.errorDownloading'),
      });
    }
  };

  const deleteFile = async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const hotel = await API.getOneHotel({ hotelId: getSelectedHotel()?.key });

      const response = await API.invoiceTemplateDeleteFile(hotel.id, hotel.chainId, state.type);

      if (response?.metadata?.httpStatusCode === 204) {
        notification.success({
          message: translate('hbxProducts.HbxUploadExampleInvoices.fileDeleted'),
        });

        setState(prev => ({
          ...prev,
          existsSingle: state.type === 'single' ? false : prev.existsSingle,
          existsMultiple: state.type === 'multiple' ? false : prev.existsMultiple,
        }));

        return true;
      }

      notification.error({
        message: translate('hbxProducts.HbxUploadExampleInvoices.fileNotDeleted'),
      });
      return false;
    } catch (err) {
      console.error(err);
      notification.error({
        message: translate('hbxProducts.HbxUploadExampleInvoices.errorDeleting'),
      });
      return false;
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const actions = {
    uploadSingleFile: file => uploadFile(file, 'single', 1),
    uploadMultipleFiles: file => uploadFile(file, 'multiple', 2),
    checkFileExists,
    downloadFile,
    deleteFile,

    getHbxSuscriptionStatus: () => {
      API.getHbxSuscriptionStatus({
        hotelId: getSelectedHotel()?.key,
        nameProduct: 'hbx_invoicing',
      }).then(res => {
        updateState({ suscription: res });
      });
    },

    setAdviceDeleteFile: show => {
      updateState({ adviceDeleteFile: show });
    },

    setType: type => {
      updateState({ type: type });
    },
  };

  useEffect(() => {
    setState(prev => ({ ...prev, isLoading: true }));
    const load = async () => {
      const existsSingle = await checkFileExists('single');
      const existsMultiple = await checkFileExists('multiple');

      setState(prev => ({ ...prev, existsSingle, existsMultiple }));
    };

    load();
    actions.getHbxSuscriptionStatus();
    setState(prev => ({ ...prev, isLoading: false }));
  }, [appState.selectedHotel]);

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}
